import React from 'react'
import {
  List,
  Datagrid,
  TextField,
  ReferenceField,
  DateField,
  TopToolbar,
  useDataProvider,
  useListContext,
  fetchStart,
  fetchEnd,
  ReferenceInput,
  SelectInput,
  Filter,
  FunctionField,
  TextInput,
} from 'react-admin'
import { DateWithDayField, TimeField } from '../components/Date'
import { CurrencyField } from '../components/Number'
import { FullNameField } from '../components/Text'
import { makeStyles } from '@material-ui/core/styles'
import CloudDownloadOutlined from '@material-ui/icons/CloudDownloadOutlined'
import CloseIcon from '@material-ui/icons/Close'
import CheckIcon from '@material-ui/icons/Check'
import ClockIcon from '@material-ui/icons/AccessTime'
import ReturnIcon from '@material-ui/icons/KeyboardReturn'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import Chip from '@material-ui/core/Chip'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Select from '@material-ui/core/Select'
import Switch from '@material-ui/core/Switch'
import Tooltip from '@material-ui/core/Tooltip'
import { useDispatch } from 'react-redux'
import { DateTime } from 'luxon'
import { config } from '../config'
import { dateFilterChange } from '../utils/dateFilters'

const useStyles = makeStyles((theme) => ({
  button: {
    margin: theme.spacing(1),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  open: {
    borderColor: '#ff9800',
    color: '#ff9800',
  },
  completed: {
    borderColor: '#4caf50',
    color: '#4caf50',
  },
  creditcard: {
    borderColor: '#2196f3',
    color: '#2196f3',
  },
  cancelled: {
    // gray color
    borderColor: '#9e9e9e',
    color: '#9e9e9e',
  },
  action: {
    borderColor: '#f50057',
    color: '#f50057',
  },
  refunded: {
    // purple color
    borderColor: '#9c27b0',
    color: '#9c27b0',
  },
}))

const BookingListActions = () => {
  const { setFilters, filterValues } = useListContext()
  const [state, setState] = React.useState({
    selectedLocation: '',
    selectedMonth: '',
  })
  const [locations, setLocations] = React.useState([])
  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const dispatch = useDispatch()
  const dataProvider = useDataProvider()
  const lastSixMonths = [...Array(6).keys()].map((i) => ({
    index: DateTime.local().month - i,
    name: DateTime.local().minus({ month: i }).toFormat('LLLL yyyy'),
  }))
  const classes = useStyles()
  const handleClickOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setState({ selectedLocation: '', selectedMonth: '' })
    setOpen(false)
  }
  const handleChange = (event) => {
    setState({ ...state, [event.target.name]: event.target.value })
  }
  const handlePeriodChange = (event) => {
    dateFilterChange(event.target.value, (dateFilter) => {
      // dateFilterChange vervangt alle filters; merge zodat locatie/naam/status
      // en de geannuleerd-toggle blijven staan
      const { key, and, ...rest } = filterValues
      setFilters({ ...rest, ...dateFilter })
    })
  }

  const showCancelled = filterValues.cancelled !== false
  const handleShowCancelledChange = (event) => {
    if (event.target.checked) {
      const { cancelled, ...rest } = filterValues
      setFilters(rest)
    } else {
      setFilters({ ...filterValues, cancelled: false })
    }
  }

  // statusfilter "geannuleerd" is onzichtbaar zolang geannuleerde boekingen
  // verborgen zijn; toon ze dan automatisch
  React.useEffect(() => {
    if (
      filterValues.status === 'cancelled' &&
      filterValues.cancelled === false
    ) {
      const { cancelled, ...rest } = filterValues
      setFilters(rest)
    }
  }, [filterValues, setFilters])

  const createCsvExport = async () => {
    setLoading(true)
    dispatch(fetchStart())
    fetch(`${config.apiUrl}/booking/csv_export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...(state.selectedLocation
          ? { locationId: state.selectedLocation }
          : {}),
        month: state.selectedMonth,
      }),
    })
      .then((response) => response.blob())
      .then((blob) => URL.createObjectURL(blob))
      .then((url) => {
        window.open(url, '_blank')
        URL.revokeObjectURL(url)
        dispatch(fetchEnd())
      })

    setLoading(false)
    handleClose()
  }

  React.useEffect(() => {
    dataProvider
      .getList(`commercial_location`, {
        pagination: { page: 1, perPage: 0 },
        sort: { field: 'name', order: 'ASC' },
      })
      .then((resp) => {
        if (resp?.data) setLocations(resp.data)
      })
  }, [dataProvider, setLocations])

  return (
    <TopToolbar>
      <FormControl className={classes.formControl}>
        <InputLabel id="period-label">Periode</InputLabel>
        <Select
          labelId="period-label"
          value={filterValues.key || ''}
          onChange={handlePeriodChange}>
          <MenuItem value={''}>Alles</MenuItem>
          <MenuItem value={'today'}>Vandaag</MenuItem>
          <MenuItem value={'nextSevenDays'}>Komende 7 dagen</MenuItem>
          <MenuItem value={'prevSevenDays'}>Afgelopen 7 dagen</MenuItem>
          <MenuItem value={'thisMonth'}>Deze maand</MenuItem>
          <MenuItem value={'prevMonth'}>Vorige maand</MenuItem>
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Switch
            checked={showCancelled}
            onChange={handleShowCancelledChange}
            disabled={filterValues.status === 'cancelled'}
            name="showCancelled"
          />
        }
        label="Toon geannuleerd"
      />
      <Button
        color="primary"
        onClick={handleClickOpen}
        size="small"
        variant="outlined"
        className={classes.button}
        endIcon={<CloudDownloadOutlined />}>
        CSV export
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Download CSV export</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Selecteer de locatie en de maand waarvoor je een export van de
            boekingen wilt ontvangen
          </DialogContentText>
          <FormControl className={classes.formControl}>
            <InputLabel id="demo-simple-select-label">Locatie</InputLabel>
            <Select
              name="selectedLocation"
              value={state.selectedLocation}
              displayEmpty
              onChange={handleChange}>
              {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel id="demo-simple-select-label">Maand</InputLabel>
            <Select
              name="selectedMonth"
              value={state.selectedMonth}
              displayEmpty
              onChange={handleChange}>
              {lastSixMonths.map((month) => (
                <MenuItem key={month.index} value={month.index}>
                  {month.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Annuleren
          </Button>
          <Button
            onClick={createCsvExport}
            color="primary"
            autoFocus
            disabled={
              loading || (!state.selectedMonth && state.selectedMonth !== 0)
            }>
            Download CSV export
          </Button>
        </DialogActions>
      </Dialog>
    </TopToolbar>
  )
}

// Puur presentatie: de status wordt server-side afgeleid en zit als
// record.status in de find-response (invi-nodejs-api #65)
const StatusField = ({ record }) => {
  const classes = useStyles()

  const status = record?.status
  if (!status) return null
  const icon =
    status === 'cancelled' || status === 'failed' ? (
      <CloseIcon htmlColor="#ff0000" />
    ) : status === 'completed' || status === 'creditcard' ? (
      <CheckIcon />
    ) : status === 'open' ? (
      <ClockIcon />
    ) : status === 'action' ? (
      <ClockIcon />
    ) : status === 'refunded' ? (
      <ReturnIcon />
    ) : null
  const className =
    status === 'cancelled'
      ? classes.cancelled
      : status === 'completed'
      ? classes.completed
      : status === 'creditcard'
      ? classes.creditcard
      : status === 'failed'
      ? classes.cancelled
      : status === 'open'
      ? classes.open
      : status === 'refunded'
      ? classes.refunded
      : status === 'action'
      ? classes.action
      : null
  const chipLabel =
    status === 'cancelled'
      ? 'geannuleerd'
      : status === 'completed'
      ? 'betaald'
      : status === 'creditcard'
      ? 'strippenkaart'
      : status === 'failed'
      ? 'mislukt'
      : status === 'action'
      ? 'actie vereist'
      : status === 'refunded'
      ? 'teruggestort'
      : 'open'

  return (
    <Chip
      variant="outlined"
      label={chipLabel}
      size="small"
      icon={icon}
      className={className}
      color="primary"
    />
  )
}

// Extern geboekt = het court-agent-reserveringsnummer dat autoPlaceBooking in
// payment_data zette. Zonder nummer kan een annulering de reservering bij de
// club niet op id verwijderen — vandaar het onderscheid in de tooltip.
const ExternalBookingField = ({ record }) => {
  const reservationId = record?.payment_data?.reservationId
  const occurenceId = record?.payment_data?.occurenceId
  if (!reservationId) {
    return (
      <Tooltip title="Geen extern reserveringsnummer bekend">
        <span style={{ color: '#9e9e9e' }}>—</span>
      </Tooltip>
    )
  }
  return (
    <Tooltip
      title={`Extern geboekt — nr. ${reservationId}${
        occurenceId ? ` (occurrence ${occurenceId})` : ''
      }`}>
      <CheckIcon htmlColor="#4caf50" fontSize="small" />
    </Tooltip>
  )
}

const BookingFilter = (props) => (
  <Filter {...props}>
    <ReferenceInput
      source="commercial_location_id"
      label="Locatie"
      reference="commercial_location"
      alwaysOn
      allowEmpty>
      <SelectInput optionText="name" />
    </ReferenceInput>
    <TextInput source="name" label="Zoek op naam" alwaysOn resettable />
    <SelectInput
      source="status"
      label="Status"
      alwaysOn
      allowEmpty
      choices={[
        { id: 'action', name: 'actie vereist' },
        { id: 'open', name: 'open' },
        { id: 'completed', name: 'betaald' },
        { id: 'creditcard', name: 'strippenkaart' },
        { id: 'refunded', name: 'teruggestort' },
        { id: 'failed', name: 'mislukt' },
        { id: 'cancelled', name: 'geannuleerd' },
      ]}
    />
  </Filter>
)

export const BookingList = (props) => (
  <List
    actions={<BookingListActions />}
    {...props}
    filters={<BookingFilter />}
    filterDefaultValues={{ cancelled: false }}
    sort={{ field: 'id', order: 'DESC' }}
    perPage={25}
    bulkActionButtons={false}
    title="Boekingen">
    <Datagrid rowClick="show">
      <TextField source="id" label="id" />
      <DateField showTime source="created" label="geboekt op" />
      <ReferenceField
        label="geboekt door"
        source="participant_id"
        reference="participant"
        link="show">
        <FullNameField firstName="firstName" lastName="lastName" />
      </ReferenceField>
      <ReferenceField
        label="locatie"
        source="commercial_location_id"
        reference="commercial_location"
        link={false}>
        <TextField source="name" />
      </ReferenceField>
      <DateWithDayField source="startDateTime" label="geboekt voor" />
      <TimeField source="startDateTime" label="van" />
      <TimeField source="endDateTime" label="tot" />
      <CurrencyField source="cost" label="prijs" />
      <FunctionField
        label="korting"
        render={(record) =>
          record.discount?.type === 'percentage'
            ? `${record.discount?.value}%`
            : record.discount?.value
            ? new Intl.NumberFormat('nl-NL', {
                style: 'currency',
                currency: 'EUR',
              }).format(record.discount?.value / 100)
            : '-'
        }
      />
      <FunctionField
        label="via"
        render={(record) =>
          record.data?.source === 'website' ? '👨‍💻' : '📲'
        }></FunctionField>
      <DateField
        source="payment_data.payment_link_send"
        label="betaallink verstuurd"
        showTime
      />
      <TextField source="payment_data.court" label="baan" />
      <ExternalBookingField label="extern" sortable={false} />
      <StatusField label="status" />
    </Datagrid>
  </List>
)
